import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import {
  FaArrowDown, FaArrowLeft, FaArrowUp, FaCertificate, FaCheckCircle,
  FaClipboardCheck, FaEdit, FaGraduationCap, FaInfoCircle, FaLaptop,
  FaPlus, FaPoll, FaTrash
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const STEP_TYPES = [
  {
    type: 'course',
    label: 'Course',
    icon: FaGraduationCap,
    color: 'primary',
    iconClassName: 'bg-blue-50 text-blue-600'
  },
  {
    type: 'assessment',
    label: 'Assessment',
    icon: FaClipboardCheck,
    color: 'success',
    iconClassName: 'bg-emerald-50 text-emerald-600'
  },
  {
    type: 'vilt',
    label: 'Virtual Instructor Led Training',
    icon: FaLaptop,
    color: 'warning',
    iconClassName: 'bg-amber-50 text-amber-600'
  },
  {
    type: 'survey',
    label: 'Survey',
    icon: FaPoll,
    color: 'info',
    iconClassName: 'bg-cyan-50 text-cyan-600'
  },
  {
    type: 'certificate',
    label: 'Certificate',
    icon: FaCertificate,
    color: 'secondary',
    iconClassName: 'bg-gray-100 text-gray-600'
  },
  {
    type: 'info',
    label: 'Custom Information Page',
    icon: FaInfoCircle,
    color: 'dark',
    iconClassName: 'bg-slate-100 text-slate-700'
  }
];

const StepCard = ({ step, index, totalSteps, moving, onEdit, onDelete, onMove }) => {
  const meta = STEP_TYPES.find((t) => t.type === step.step_type) || STEP_TYPES[0];
  const Icon = meta.icon;

  const subtitle = (() => {
    if (step.step_type === 'course') {
      const courseName = step.courseModules?.[0]?.course?.name;
      const count = step.courseModules?.length || 0;
      return courseName ? `${courseName} · ${count} module(s)` : 'Course not configured';
    }
    if (step.step_type === 'assessment') return step.assessmentLink?.assessment?.title || 'Assessment not selected';
    if (step.step_type === 'vilt') return step.viltLink?.vlt?.title || 'VILT not selected';
    if (step.step_type === 'survey') return step.surveyLink?.survey?.title || 'Survey not selected';
    if (step.step_type === 'certificate') return step.certificateLink?.template?.name || 'Certificate not selected';
    if (step.step_type === 'info') return step.title || 'Information page';
    return '';
  })();

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-purple-100 bg-white p-3 shadow-sm transition-shadow hover:shadow-md sm:flex-row sm:items-center"
    >
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-purple-600 text-sm font-bold text-white shadow-sm">
        {index + 1}
      </span>
      <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${meta.iconClassName}`}>
        <Icon aria-hidden="true" />
      </div>
      <div className="flex-grow-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge bg={meta.color}>{meta.label}</Badge>
          {step.is_mandatory ? <Badge bg="danger">Mandatory</Badge> : <Badge bg="light" text="dark">Optional</Badge>}
          <span className="text-xs text-gray-400">#{step.sequence}</span>
        </div>
        <div className="fw-semibold text-gray-800 mt-1">{step.title || meta.label}</div>
        <div className="text-sm text-gray-500">{subtitle}</div>
      </div>
      <div className="flex items-center gap-1.5 self-end sm:self-auto">
        <Button
          bsPrefix="custom-hamburger"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white p-0 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
          onClick={() => onMove(index, -1)}
          disabled={moving || index === 0}
          aria-label={`Move ${step.title || meta.label} up`}
          title="Move up"
        >
          <FaArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <Button
          bsPrefix="custom-hamburger"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white p-0 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
          onClick={() => onMove(index, 1)}
          disabled={moving || index === totalSteps - 1}
          aria-label={`Move ${step.title || meta.label} down`}
          title="Move down"
        >
          <FaArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <Button
          bsPrefix="custom-hamburger"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-purple-300 bg-white p-0 text-purple-600 hover:bg-purple-50"
          onClick={() => onEdit(step)}
          aria-label="Configure step"
          title="Configure step"
        >
          <FaEdit className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <Button
          bsPrefix="custom-hamburger"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-300 bg-white p-0 text-red-600 hover:bg-red-50"
          onClick={() => onDelete(step)}
          aria-label="Delete step"
          title="Delete step"
        >
          <FaTrash className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};

const emptyStepForm = {
  step_type: 'course',
  title: '',
  description: '',
  is_mandatory: true,
  course_id: '',
  module_ids: [],
  assessment_id: '',
  vlt_id: '',
  resource_ids: [],
  survey_id: '',
  certificate_template_id: ''
};

const extractList = (response, nestedKey) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.[nestedKey]) ? payload[nestedKey] : [];
};

const LearningJourneyBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState(emptyStepForm);
  const [saving, setSaving] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [vlts, setVlts] = useState([]);
  const [relatedVlts, setRelatedVlts] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [templates, setTemplates] = useState([]);

  const loadJourney = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.learningJourneys.getById(id);
      const data = res.data?.data;
      setJourney(data);
      setSteps([...(data?.steps || [])].sort((a, b) => a.sequence - b.sequence));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load journey builder');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadJourney();
  }, [loadJourney]);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [cRes, aRes, vRes, sRes, tRes] = await Promise.allSettled([
          api.courses.getAll(),
          api.assessments.getAll(),
          api.vlt.getAll(),
          api.surveys.getAll(),
          api.certificates.getTemplates({ status: 'active', limit: 100 })
        ]);
        if (cRes.status === 'fulfilled') {
          const raw = cRes.value.data?.data || cRes.value.data || [];
          setCourses(Array.isArray(raw) ? raw.filter((c) => c.deleted !== 1 && c.deleted !== true) : []);
        }
        if (aRes.status === 'fulfilled') {
          const raw = aRes.value.data?.data || aRes.value.data || [];
          setAssessments(Array.isArray(raw) ? raw : []);
        }
        if (vRes.status === 'fulfilled') {
          const raw = vRes.value.data?.data || vRes.value.data || [];
          setVlts(Array.isArray(raw) ? raw : []);
        }
        if (sRes.status === 'fulfilled') {
          const raw = sRes.value.data?.data || sRes.value.data || [];
          setSurveys(Array.isArray(raw) ? raw : []);
        }
        if (tRes.status === 'fulfilled') {
          setTemplates(extractList(tRes.value, 'templates'));
        }
      } catch {
        // masters optional for display
      }
    };
    loadMasters();
  }, []);

  const loadModulesForCourse = async (courseId) => {
    if (!courseId) {
      setModules([]);
      return;
    }
    try {
      const res = await api.modules.getByCourse(courseId);
      const raw = res.data?.data || res.data || [];
      setModules(Array.isArray(raw) ? raw.filter((m) => !m.deleted) : []);
    } catch {
      setModules([]);
    }
  };

  useEffect(() => {
    if (stepForm.course_id) loadModulesForCourse(stepForm.course_id);
  }, [stepForm.course_id]);

  useEffect(() => {
    if (!stepForm.vlt_id) {
      setRelatedVlts([]);
      return;
    }
    // Related resources: same course_id or same org — fall back to all active VLTs except primary
    const primary = vlts.find((v) => String(v.id) === String(stepForm.vlt_id));
    const related = vlts.filter((v) => {
      if (String(v.id) === String(stepForm.vlt_id)) return true;
      if (primary?.course_id && v.course_id === primary.course_id) return true;
      return ['document', 'video', 'link'].includes(v.type);
    });
    setRelatedVlts(related.length ? related : vlts);
  }, [stepForm.vlt_id, vlts]);

  const handleAddType = (type) => {
    setEditingStep(null);
    setStepForm({ ...emptyStepForm, step_type: type, title: STEP_TYPES.find((t) => t.type === type)?.label || '' });
    setShowTypeModal(false);
    setShowConfigModal(true);
  };

  const handleEdit = (step) => {
    setEditingStep(step);
    setStepForm({
      step_type: step.step_type,
      title: step.title || '',
      description: step.description || '',
      is_mandatory: step.is_mandatory !== false,
      course_id: step.courseModules?.[0]?.course_id || step.reference_id || '',
      module_ids: (step.courseModules || []).map((m) => m.module_id),
      assessment_id: step.assessmentLink?.assessment_id || step.reference_id || '',
      vlt_id: step.viltLink?.vlt_id || step.reference_id || '',
      resource_ids: (step.resources || []).map((r) => r.vlt_id),
      survey_id: step.surveyLink?.survey_id || step.reference_id || '',
      certificate_template_id: step.certificateLink?.certificate_template_id || step.reference_id || ''
    });
    setShowConfigModal(true);
  };

  const handleDelete = async () => {
    if (!stepToDelete) return;
    try {
      setDeleting(true);
      const res = await api.learningJourneys.deleteStep(id, stepToDelete.id);
      setJourney(res.data?.data);
      setSteps([...(res.data?.data?.steps || [])].sort((a, b) => a.sequence - b.sequence));
      setStepToDelete(null);
      toast.success('Step deleted');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete step');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveStep = async () => {
    const payload = {
      step_type: stepForm.step_type,
      title: stepForm.title,
      description: stepForm.description,
      is_mandatory: stepForm.is_mandatory,
      course_id: stepForm.course_id ? Number(stepForm.course_id) : undefined,
      module_ids: stepForm.module_ids.map(Number),
      assessment_id: stepForm.assessment_id ? Number(stepForm.assessment_id) : undefined,
      vlt_id: stepForm.vlt_id ? Number(stepForm.vlt_id) : undefined,
      resource_ids: stepForm.resource_ids.map(Number),
      survey_id: stepForm.survey_id ? Number(stepForm.survey_id) : undefined,
      certificate_template_id: stepForm.certificate_template_id
        ? Number(stepForm.certificate_template_id)
        : undefined
    };

    try {
      setSaving(true);
      const res = editingStep
        ? await api.learningJourneys.updateStep(id, editingStep.id, payload)
        : await api.learningJourneys.addStep(id, payload);
      setJourney(res.data?.data);
      setSteps([...(res.data?.data?.steps || [])].sort((a, b) => a.sequence - b.sequence));
      setShowConfigModal(false);
      toast.success(editingStep ? 'Step updated' : 'Step added');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveStep = async (currentIndex, direction) => {
    const nextIndex = currentIndex + direction;
    if (reordering || nextIndex < 0 || nextIndex >= steps.length) return;

    const previousSteps = steps;
    const reordered = [...steps];
    [reordered[currentIndex], reordered[nextIndex]] = [reordered[nextIndex], reordered[currentIndex]];
    const sequencedSteps = reordered.map((step, index) => ({ ...step, sequence: index + 1 }));

    setSteps(sequencedSteps);
    try {
      setReordering(true);
      await api.learningJourneys.reorderSteps(id, sequencedSteps.map((step) => step.id));
      toast.success('Step order updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reorder steps');
      setSteps(previousSteps);
    } finally {
      setReordering(false);
    }
  };

  const toggleModule = (moduleId) => {
    setStepForm((prev) => {
      const exists = prev.module_ids.includes(moduleId);
      return {
        ...prev,
        module_ids: exists
          ? prev.module_ids.filter((m) => m !== moduleId)
          : [...prev.module_ids, moduleId]
      };
    });
  };

  const toggleResource = (vltId) => {
    setStepForm((prev) => {
      const exists = prev.resource_ids.includes(vltId);
      return {
        ...prev,
        resource_ids: exists
          ? prev.resource_ids.filter((m) => m !== vltId)
          : [...prev.resource_ids, vltId]
      };
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <Spinner animation="border" role="status" className="h-12 w-12 text-purple-600">
          <span className="visually-hidden">Loading journey builder...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-3 md:p-6 lg:p-8">
      <div className="mb-4">
        <Row className="align-items-center g-3">
          <Col md={2}>
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => navigate('/admin/learning-journeys')}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-300 bg-white px-4 py-2 text-sm font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg"
              aria-label="Back to learning journeys"
            >
              <FaArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </Button>
          </Col>
          <Col md={7}>
            <h5 className="m-0 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 md:text-3xl">
              Journey Builder
            </h5>
            <p className="mb-0 mt-1 text-sm text-gray-600">{journey?.name} · {journey?.code}</p>
          </Col>
          <Col md={3}>
            <Button
              bsPrefix="custom-hamburger"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-purple-600 bg-white p-2 text-sm font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg"
              onClick={() => setShowTypeModal(true)}
              aria-label="Add step"
            >
              <FaPlus aria-hidden="true" /> Add Step
            </Button>
          </Col>
        </Row>
      </div>

      <section className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Journey builder summary">
        <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <FaGraduationCap aria-hidden="true" />
          </span>
          <div>
            <p className="mb-0 text-2xl font-bold text-slate-900">{steps.length}</p>
            <p className="mb-0 text-sm text-slate-500">Total steps</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <FaCheckCircle aria-hidden="true" />
          </span>
          <div>
            <p className="mb-0 text-2xl font-bold text-slate-900">
              {steps.filter((step) => step.is_mandatory).length}
            </p>
            <p className="mb-0 text-sm text-slate-500">Mandatory steps</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <FaInfoCircle aria-hidden="true" />
          </span>
          <div>
            <Badge bg={journey?.status === 1 ? 'success' : journey?.status === 2 ? 'warning' : 'secondary'}>
              {journey?.status === 1 ? 'Active' : journey?.status === 2 ? 'Draft' : 'Inactive'}
            </Badge>
            <p className="mb-0 mt-1 text-sm text-slate-500">Journey status</p>
          </div>
        </div>
      </section>

      <div className="rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-2xl backdrop-blur-sm">
        {steps.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-3">No steps yet. Build your guided learning sequence.</p>
            <Button variant="outline-primary" onClick={() => setShowTypeModal(true)}>
              <FaPlus className="me-2" aria-hidden="true" /> Add First Step
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Use the <strong>up</strong> and <strong>down</strong> arrow buttons to change the step order.
            </div>
            {steps.map((step, index) => (
              <StepCard
                key={step.id}
                step={step}
                index={index}
                totalSteps={steps.length}
                moving={reordering}
                onMove={handleMoveStep}
                onEdit={handleEdit}
                onDelete={setStepToDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Step type picker */}
      <Modal show={showTypeModal} onHide={() => setShowTypeModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" className="border-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-white modal-header">
          <Modal.Title className="text-xl font-bold">Add Step</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-grid gap-2 bg-gray-50 p-4">
          {STEP_TYPES.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.type}
                bsPrefix="custom-hamburger"
                className="d-flex align-items-center gap-3 rounded-xl border border-purple-100 bg-white py-3 text-start text-gray-700 shadow-sm transition-all hover:border-purple-300 hover:bg-purple-50"
                onClick={() => handleAddType(item.type)}
                aria-label={`Add ${item.label} step`}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${item.iconClassName}`}>
                  <Icon aria-hidden="true" />
                </span>
                <span className="fw-semibold">{item.label}</span>
              </Button>
            );
          })}
        </Modal.Body>
      </Modal>

      {/* Step config */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="border-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-white modal-header">
          <Modal.Title className="text-xl font-bold">
            {editingStep ? 'Configure Step' : 'New Step'} — {STEP_TYPES.find((t) => t.type === stepForm.step_type)?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-4">
          <Form.Group className="mb-3">
            <Form.Label>Step Title</Form.Label>
            <Form.Control
              value={stepForm.title}
              onChange={(e) => setStepForm((p) => ({ ...p, title: e.target.value }))}
              aria-label="Step title"
            />
          </Form.Group>
          <Form.Check
            type="switch"
            className="mb-3"
            id="is_mandatory"
            label="Mandatory step"
            checked={stepForm.is_mandatory}
            onChange={(e) => setStepForm((p) => ({ ...p, is_mandatory: e.target.checked }))}
          />

          {stepForm.step_type === 'info' && (
            <Form.Group className="mb-3">
              <Form.Label>Information Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={stepForm.description}
                onChange={(e) => setStepForm((p) => ({ ...p, description: e.target.value }))}
                aria-label="Information content"
              />
            </Form.Group>
          )}

          {stepForm.step_type === 'course' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select Course (from Course Master)</Form.Label>
                <Form.Select
                  value={stepForm.course_id}
                  onChange={(e) => setStepForm((p) => ({ ...p, course_id: e.target.value, module_ids: [] }))}
                  aria-label="Select course"
                >
                  <option value="">Choose course...</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {stepForm.course_id && (
                <div className="border rounded-xl p-3 bg-light">
                  <div className="fw-semibold mb-2">Modules (Sub Courses) — select required only</div>
                  {modules.length === 0 ? (
                    <div className="text-muted small">No modules found for this course.</div>
                  ) : (
                    modules.map((m) => (
                      <Form.Check
                        key={m.id}
                        type="checkbox"
                        id={`mod-${m.id}`}
                        className="mb-2"
                        label={m.name}
                        checked={stepForm.module_ids.includes(m.id) || stepForm.module_ids.includes(String(m.id))}
                        onChange={() => toggleModule(m.id)}
                      />
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {stepForm.step_type === 'assessment' && (
            <Form.Group className="mb-3">
              <Form.Label>Select Assessment (from Assessment Master)</Form.Label>
              <Form.Select
                value={stepForm.assessment_id}
                onChange={(e) => setStepForm((p) => ({ ...p, assessment_id: e.target.value }))}
                aria-label="Select assessment"
              >
                <option value="">Choose assessment...</option>
                {assessments.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {stepForm.step_type === 'vilt' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select VILT</Form.Label>
                <Form.Select
                  value={stepForm.vlt_id}
                  onChange={(e) => setStepForm((p) => ({ ...p, vlt_id: e.target.value, resource_ids: [] }))}
                  aria-label="Select VILT"
                >
                  <option value="">Choose VILT...</option>
                  {vlts.map((v) => (
                    <option key={v.id} value={v.id}>{v.title} ({v.type})</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {stepForm.vlt_id && (
                <div className="border rounded-xl p-3 bg-light">
                  <div className="fw-semibold mb-2">Resources (Documents / Videos / Links / Meetings)</div>
                  {relatedVlts.map((r) => (
                    <Form.Check
                      key={r.id}
                      type="checkbox"
                      id={`res-${r.id}`}
                      className="mb-2"
                      label={`${r.title} · ${r.type}${r.meeting_datetime ? ' · Meeting' : ''}`}
                      checked={stepForm.resource_ids.includes(r.id) || stepForm.resource_ids.includes(String(r.id))}
                      onChange={() => toggleResource(r.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {stepForm.step_type === 'survey' && (
            <Form.Group className="mb-3">
              <Form.Label>Select Survey (from Survey Master)</Form.Label>
              <Form.Select
                value={stepForm.survey_id}
                onChange={(e) => setStepForm((p) => ({ ...p, survey_id: e.target.value }))}
                aria-label="Select survey"
              >
                <option value="">Choose survey...</option>
                {surveys.map((s) => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {stepForm.step_type === 'certificate' && (
            <Form.Group className="mb-3">
              <Form.Label>Select Certificate Template</Form.Label>
              <Form.Select
                value={stepForm.certificate_template_id}
                onChange={(e) => setStepForm((p) => ({ ...p, certificate_template_id: e.target.value }))}
                aria-label="Select certificate template"
              >
                <option value="">Choose template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Form.Select>
              <Form.Text muted>Issued only after the entire journey is completed.</Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-gray-100 p-4">
          <Row className="w-100 g-3">
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border-2 border-gray-300 bg-gray-100 p-2 font-semibold text-gray-700 shadow-md hover:bg-gray-200" onClick={() => setShowConfigModal(false)}>
                Cancel
              </Button>
            </Col>
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border border-purple-600 bg-white p-2 font-semibold text-purple-600 shadow-md hover:bg-purple-50" onClick={handleSaveStep} disabled={saving}>
                {saving ? <Spinner size="sm" animation="border" /> : 'Save Step'}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(stepToDelete)} onHide={() => !deleting && setStepToDelete(null)} centered>
        <Modal.Header closeButton={!deleting} closeVariant="white" className="border-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-white modal-header">
          <Modal.Title className="text-xl font-bold">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 py-10 text-center">
          <FaTrash className="mx-auto mb-4 text-5xl text-red-500" aria-hidden="true" />
          <h5 className="mb-2 font-bold text-gray-800">Delete Journey Step</h5>
          <p className="mb-0 text-gray-600">
            Are you sure you want to delete <strong>{stepToDelete?.title || stepToDelete?.step_type}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-gray-100 p-4">
          <Row className="w-100 g-3">
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border-2 border-gray-300 bg-gray-100 p-2 font-semibold text-gray-700 shadow-md hover:bg-gray-200" disabled={deleting} onClick={() => setStepToDelete(null)}>
                Cancel
              </Button>
            </Col>
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border border-red-500 bg-white p-2 font-semibold text-red-600 shadow-md hover:bg-red-50" disabled={deleting} onClick={handleDelete}>
                {deleting ? <Spinner size="sm" animation="border" /> : 'Delete Step'}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default LearningJourneyBuilder;
